import * as React from 'react';
import { Button, Display } from '../Button/Button';
import { TextField } from '../TextBox/TextBox';
import { observer } from 'mobx-react';
import { action, observable, makeObservable } from 'mobx';
import If from 'Views/Components/If/If';

export interface CommonPaginationOptions {
	/** Number of records per page. */
	perPage?: number;
	/** The page number of the collection. If this field is set then the pagination becomes controlled externally. */
	pageNo?: number;
	/**
	 * The total number of records in the overall collection without pagination. If this field is not set then the size
	 * of the passed in model collection is used.
	 */
	totalRecords?: number;
	/** Function to call on page change */
	onPageChange?: (pageNo: number) => void;
	/**
	 * Should the go to page text box be shown
	 */
	showGoToPageBox?: boolean;
}

export interface IPaginationProps extends Required<CommonPaginationOptions> {
	staffBookingDisplay?: boolean;
	showFirstArrow?: boolean;
	showLastArrow?: boolean;
	leftArrowIcon?: string;
	rightArrowIcon?: string;
}

enum validPageOptions {
	HIGH,
	VALID,
	LOW,
}

class Pagination extends React.Component<IPaginationProps> {
	@observable
	private pageTextBoxModel = { page: '1' };

	constructor(props: IPaginationProps) {
		super(props);

		makeObservable(this);
	}

	public render() {
		const {
			pageNo,
			staffBookingDisplay,
			showFirstArrow = true,
			showLastArrow = true,
			leftArrowIcon = null,
			rightArrowIcon = null,
		} = this.props;

		if (staffBookingDisplay) {
			return (
				<nav aria-label="pagination pagination__collections">
					{this.renderFirstButton()}
					{this.renderPreviousButton()}
					<span className="pagination__page-number">
						{pageNo + 1} of {this.totalPages}
					</span>
					{this.renderNextButton()}
					{this.renderLastButton()}
				</nav>
			);
		}

		return (
			<nav aria-label="pagination pagination__collections">
				<ul className="collection__pagination">
					<If condition={showFirstArrow}>
						<li>{this.renderFirstButton()}</li>
					</If>
					<li>{this.renderPreviousButton(leftArrowIcon)}</li>
					<li>
						<span className="pagination__page-number">
							{pageNo + 1} of {this.totalPages}
						</span>
					</li>
					<li>{this.renderNextButton(rightArrowIcon)}</li>
					<If condition={showLastArrow}>
						<li>{this.renderLastButton()}</li>
					</If>
				</ul>
			</nav>
		);
	}

	public renderFirstButton = () => {
		const { pageNo } = this.props;
		const isFirstPage = pageNo === 0;
		return (
			<Button
				disabled={isFirstPage}
				onClick={this.firstPage}
				display={Display.Text}
				labelVisible={false}
				icon={{ icon: 'chevrons-left', iconPos: 'icon-left' }}
			>
				First
			</Button>
		);
	};

	public renderNextButton = (iconString?: string | null) => {
		const { pageNo } = this.props;
		const noNextPage = pageNo >= this.totalPages - 1;
		return (
			<Button
				onClick={this.nextPage}
				display={Display.Text}
				disabled={noNextPage}
				labelVisible={false}
				icon={{ icon: iconString ?? 'chevron-right', iconPos: 'icon-right' }}
			>
				Next
			</Button>
		);
	};

	public renderPreviousButton = (iconString?: string | null) => {
		const { pageNo } = this.props;
		const noPreviousPage = pageNo < 1;
		return (
			<Button
				onClick={this.previousPage}
				display={Display.Text}
				disabled={noPreviousPage}
				labelVisible={false}
				icon={{ icon: iconString ?? 'chevron-left', iconPos: 'icon-left' }}
			>
				Previous
			</Button>
		);
	};

	public renderLastButton = () => {
		const { pageNo } = this.props;
		const isLastPage = pageNo >= this.totalPages - 1;
		return (
			<Button
				onClick={this.lastPage}
				display={Display.Text}
				disabled={isLastPage}
				labelVisible={false}
				icon={{ icon: 'chevrons-right', iconPos: 'icon-right' }}
			>
				Last
			</Button>
		);
	};

	public renderGoToPageBox = () => {
		if (this.props.showGoToPageBox) {
			return (
				<form className="paginator__go-to-pg" onSubmit={this.onGoToPageFormSubmit}>
					<TextField
						model={this.pageTextBoxModel}
						modelProperty="page"
						label="Go to page"
						inputProps={{ type: 'number' }}
					/>
				</form>
			);
		}

		return null;
	};

	private firstPage = () => {
		this.gotoPage(0);
	};

	private previousPage = () => {
		const { pageNo } = this.props;
		this.gotoPage(pageNo - 1);
	};

	private nextPage = () => {
		const { pageNo } = this.props;
		this.gotoPage(pageNo + 1);
	};

	private lastPage = () => {
		this.gotoPage(this.totalPages - 1);
	};

	private onGoToPageFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const n = Number.parseInt(this.pageTextBoxModel.page, 10);
		if (!Number.isNaN(n)) {
			this.gotoPage(n - 1);
		}
	};

	@action
	public gotoPage = (pageNo: number) => {
		const { onPageChange } = this.props;

		const validPage = this.isValidPage(pageNo);

		let correctedPageNo = pageNo;
		if (validPage === validPageOptions.HIGH) {
			correctedPageNo = this.totalPages - 1;
		} else if (validPage === validPageOptions.LOW) {
			correctedPageNo = 0;
		}

		onPageChange(correctedPageNo);
	};

	private isValidPage = (pageNo: number): validPageOptions => {
		if (pageNo >= this.totalPages) {
			return validPageOptions.HIGH;
		}

		if (pageNo < 0) {
			return validPageOptions.LOW;
		}

		return validPageOptions.VALID;
	};

	private get totalPages() {
		const { perPage, totalRecords } = this.props;

		if (totalRecords > 0) {
			return Math.ceil(totalRecords / perPage);
		}

		return 1;
	}
}

export default observer(Pagination);
